import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Plus, TrendingDown, TrendingUp, Wallet as WalletIcon } from 'lucide-react';
import { InFlight } from '../components/InFlight';
import { gauntlet } from '../lib/api';
import {
  chainColor,
  cx,
  formatAmount,
  formatFiat,
  formatPercent,
  timeAgo,
} from '../lib/format';
import { Badge, Button, ChainAvatar, EmptyState, Panel, Skeleton } from '../components/ui';
import { CreateWalletDialog } from '../components/CreateWalletDialog';

export function DashboardPage() {
  const [creating, setCreating] = useState(false);
  const queryClient = useQueryClient();

  const { data: portfolio, isLoading: loadingPortfolio } = useQuery({
    queryKey: ['portfolio'],
    queryFn: () => gauntlet.portfolio(),
    refetchInterval: 60_000,
  });

  const { data: walletData, isLoading: loadingWallets } = useQuery({
    queryKey: ['wallets'],
    queryFn: () => gauntlet.wallets(),
  });

  const { data: historyData } = useQuery({
    queryKey: ['portfolio-history'],
    queryFn: () => gauntlet.portfolioHistory(90),
  });

  const createWallet = useMutation({
    mutationFn: gauntlet.createWallet,
    onSuccess: () => queryClient.invalidateQueries(),
  });

  const wallets = walletData?.wallets ?? [];
  const holdings = portfolio?.holdings ?? [];
  const positive = (portfolio?.change24hPercent ?? 0) >= 0;

  const chartData = useMemo(
    () =>
      (historyData?.history ?? []).map((point) => ({
        day: point.day,
        value: Number(point.value) / 100,
      })),
    [historyData],
  );

  const allocation = useMemo(
    () =>
      holdings.slice(0, 8).map((h) => ({
        name: h.symbol,
        value: Number(h.value) / 100,
        chainId: h.chainId,
      })),
    [holdings],
  );

  if (loadingWallets) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (wallets.length === 0) {
    return (
      <>
        <Panel>
          <EmptyState
            icon={<WalletIcon size={30} />}
            title="No wallets yet"
            description="Create your first wallet to generate addresses across 130+ chains from a single recovery phrase."
            action={
              <Button variant="primary" icon={<Plus size={15} />} onClick={() => setCreating(true)}>
                Create a wallet
              </Button>
            }
          />
        </Panel>
        <CreateWalletDialog
          open={creating}
          onClose={() => setCreating(false)}
          onCreate={createWallet.mutateAsync}
        />
      </>
    );
  }

  return (
    <div className="space-y-6">
      {/* Money in flight, before anything else — it is the most time-sensitive
          thing on the page. */}
      <InFlight compact />

      {/* Headline value */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Panel className="lg:col-span-2" padded={false}>
          <div className="p-6">
            <p className="label-xs">Total portfolio value</p>
            {loadingPortfolio ? (
              <Skeleton className="mt-2 h-10 w-52" />
            ) : (
              <div className="mt-1.5 flex flex-wrap items-baseline gap-3">
                <span className="num text-4xl font-semibold tracking-tight">
                  {formatFiat(portfolio?.totalValue ?? '0', portfolio?.currency)}
                </span>
                {portfolio && Number(portfolio.totalValue) > 0 && (
                  <span
                    className={cx(
                      'num inline-flex items-center gap-1 text-sm font-medium',
                      positive ? 'text-gain' : 'text-loss',
                    )}
                  >
                    {positive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                    {formatPercent(portfolio.change24hPercent)}
                    <span className="text-ink-faint">
                      ({formatFiat(portfolio.change24hValue, portfolio.currency)})
                    </span>
                  </span>
                )}
              </div>
            )}
            <p className="mt-1 text-xs text-ink-faint">
              {portfolio
                ? `${portfolio.assetCount} asset${portfolio.assetCount === 1 ? '' : 's'} across ${portfolio.chainCount} chain${portfolio.chainCount === 1 ? '' : 's'} · updated ${timeAgo(portfolio.updatedAt)}`
                : '—'}
            </p>
          </div>

          {chartData.length > 1 && (
            <div className="h-40 px-2 pb-2">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 4, right: 8, bottom: 4, left: 8 }}>
                  <defs>
                    <linearGradient id="valueFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--color-brass)" stopOpacity={0.28} />
                      <stop offset="100%" stopColor="var(--color-brass)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="day" hide />
                  <YAxis hide domain={['dataMin', 'dataMax']} />
                  <Tooltip
                    contentStyle={{
                      background: 'var(--color-raised)',
                      border: '1px solid var(--color-hairline)',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    labelFormatter={(day) => new Date(Number(day)).toLocaleDateString()}
                    formatter={(value) => [
                      formatFiat(String(Math.round(Number(value ?? 0) * 100)), portfolio?.currency),
                      'Value',
                    ]}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="var(--color-brass)"
                    strokeWidth={1.75}
                    fill="url(#valueFill)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>

        <Panel title="Allocation">
          {allocation.length === 0 ? (
            <p className="py-10 text-center text-sm text-ink-faint">
              Nothing to show until a balance arrives.
            </p>
          ) : (
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={allocation}
                    dataKey="value"
                    nameKey="name"
                    innerRadius="58%"
                    outerRadius="88%"
                    paddingAngle={2}
                    stroke="none"
                  >
                    {allocation.map((entry) => (
                      <Cell key={entry.name} fill={chainColor(entry.chainId)} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: 'var(--color-raised)',
                      border: '1px solid var(--color-hairline)',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    formatter={(value, name) => [
                      formatFiat(String(Math.round(Number(value ?? 0) * 100)), portfolio?.currency),
                      String(name ?? ''),
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>
      </div>

      {/* Holdings */}
      <Panel
        title="Holdings"
        padded={false}
        action={
          <Button size="sm" icon={<Plus size={14} />} onClick={() => setCreating(true)}>
            New wallet
          </Button>
        }
      >
        {holdings.length === 0 ? (
          <EmptyState
            title="No balances found yet"
            description="Press Sync to check your addresses against the chains you have enabled."
          />
        ) : (
          <ul className="divide-y divide-hairline">
            {holdings.map((holding) => (
              <li
                key={`${holding.chainId}:${holding.token ?? 'native'}`}
                className="flex items-center gap-3 px-5 py-3.5"
              >
                <ChainAvatar chainId={holding.chainId} symbol={holding.symbol} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium">{holding.symbol}</span>
                    {holding.stale && <Badge tone="warn">stale price</Badge>}
                  </div>
                  <p className="truncate text-xs text-ink-faint">{holding.name}</p>
                </div>
                <div className="text-right">
                  <p className="num text-sm">
                    {formatAmount(holding.amount, holding.decimals)}{' '}
                    <span className="text-ink-faint">{holding.symbol}</span>
                  </p>
                  <p className="num text-xs text-ink-faint">
                    {formatFiat(holding.value, portfolio?.currency)}
                    {holding.change24h != null && (
                      <span className={cx('ml-2', holding.change24h >= 0 ? 'text-gain' : 'text-loss')}>
                        {formatPercent(holding.change24h)}
                      </span>
                    )}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {/* Wallets */}
      <Panel title="Wallets" padded={false}>
        <ul className="divide-y divide-hairline">
          {wallets.map((wallet) => (
            <li key={wallet.id}>
              <Link
                to={`/wallets/${wallet.id}`}
                className="flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-raised"
              >
                <div className="flex size-9 items-center justify-center rounded-lg border border-hairline bg-raised">
                  <WalletIcon size={16} className="text-ink-dim" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium">{wallet.name}</span>
                    {wallet.type === 'watch' && <Badge>watch-only</Badge>}
                    {wallet.has_passphrase === 1 && <Badge tone="brass">passphrase</Badge>}
                  </div>
                  <p className="text-xs text-ink-faint">
                    {wallet.accounts.length} chain{wallet.accounts.length === 1 ? '' : 's'}
                    {wallet.fingerprint && ` · ${wallet.fingerprint}`}
                  </p>
                </div>
                <Badge tone={wallet.unlocked ? 'gain' : 'neutral'}>
                  {wallet.unlocked ? 'unlocked' : 'locked'}
                </Badge>
              </Link>
            </li>
          ))}
        </ul>
      </Panel>

      <CreateWalletDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreate={createWallet.mutateAsync}
      />
    </div>
  );
}
